import _ from "lodash";
import { get } from "dot-prop-immutable";

/* Das lokale Modell merkt sich nach jeder Änderung, ob es jetzt
 * "in_sync" war. Wenn ein neues Modell angeboten wird, steht der
 * alte Wert zum Vergleich nicht mehr zur Verfügung. Aus einem
 * Modell
 * { a: "abc", b: "def" }
 * wird deswegen
 * { a: { value: "abc", in_sync: true },
 *   b: { value: "def", in_sync: true } }
 */
const makeLocal = (value, in_sync = true) => ({
	value, in_sync
});

/* Zahlen in model werden immer über Texteingaben realisiert, weil
 * nur so ein leeres Eingabefeld möglich ist. Dieser Fall kann während
 * der Eingabe auftreten, und wird bei der Bedienung auch erwartet.
 * Dass dann die Validierung nicht klappt, ist dann natürlich auch
 * zu beachten.
 */
const modelToLocal = model_value => (
	_.isNumber(model_value) ? model_value.toString() : model_value
);

const localFromModel = (default_local, model) => {
	let local = {};
	for (let prop in default_local) {
		if (_.isArray(default_local[prop])) {
			local[prop] = [];
			for (let i = 0; i < default_local[prop].length; i += 1) {
				const item = default_local[prop][i];
				const m = model[prop] ? model[prop][i] : undefined;
				local[prop].push(localFromModel(item, m));
			}
		} else if (_.isObject(default_local[prop])) {
			local[prop] = localFromModel(default_local[prop], model[prop]);
		} else {
			local[prop] = makeLocal(
				(_.isUndefined(model) || _.isNull(model) || _.isUndefined(model[prop]))
					? default_local[prop]
					: modelToLocal(model[prop]));
		}
	}
	return local;
};

/* default_local hat nicht die Sonderstruktur mit { value, in_sync },
 * weil dann localFromModel() einfacher ist.
 */
export const stateFromModel = (default_local, model, validate) => {
	const local = localFromModel(default_local, model);
	return {
		model: model,
		local: local,
		any_change: false,
		all_ok: validate(local)
	};
};

const isRecursiveObject = object => (
	_.isObject(object) &&
	object.value === undefined &&
	object.in_sync === undefined
);

export const modelFromLocal = local => {
	if (!_.isObject(local)) {
		return local;
	}
	let model = {};
	for (let p in local) {
		if (_.isArray(local[p])) {
			model[p] = [];
			for (let item of local[p]) {
				model[p].push(modelFromLocal(item));
			}
		} else if (isRecursiveObject(local[p])) {
			model[p] = modelFromLocal(local[p]);
		} else {
			model[p] = local[p].value;
		}
	}
	return model;
};

const anyChange = (local, model) => {
	if (!model) {
		return false;
	}
	for (let prop in local) {
		if (_.isArray(local[prop])) {
			for (let i = 0; i < local[prop].length; i += 1) {
				let m = model[prop] ? model[prop][i] : undefined;
				if (anyChange(local[prop][i], m)) {
					return true;
				}
			}
		} else if (isRecursiveObject(local[prop])) {
			if (anyChange(local[prop], model[prop])) {
				return true;
			}
		} else {
			if (!local[prop].in_sync) {
				return true;
			}
		}
	}
	return false;
};

export const changeState = (path, value, state, validate) => {
	let parts = path.split(".").reverse();
	let new_state = {
		[parts.shift()]: makeLocal(value,
			modelToLocal(get(state.model, path)) === value)
	};
	while (parts.length) {
		new_state = {
			[parts.shift()]: new_state
		};
	}
	new_state = {
		local: new_state
	};
	/* React.Component setState() arbeitet nicht rekursiv, deswegen den vollständigen
	 * neuen State zurück gegeben.
	 */
	let next_state = _.merge({}, state, new_state);
	next_state.all_ok = validate(next_state.local);
	next_state.any_change = anyChange(next_state.local, state.model);
	return next_state;
};

const recheckLocal = (local, new_model) => {
	let changes = null;
	for (let prop in local) {
		if (_.isArray(local[prop])) {
			let subchanges = [];
			for (let i = 0; i < local[prop].length; i += 1) {
				let m = (new_model && new_model[prop]) ? new_model[prop][i] : undefined; 
				let item_changes = recheckLocal(local[prop][i], m);
				if (item_changes) {
					subchanges[i] = item_changes;
				}
			}
			if (subchanges.length) {
				changes = _.merge(changes, { [prop]: subchanges });
			}
		} else if (isRecursiveObject(local[prop])) {
			const subchanges = recheckLocal(local[prop], new_model[prop]);
			if (subchanges) {
				changes = _.merge(changes, { [prop]: subchanges });
			}
		} else {
			/* Durch die JSON Übertragung wird aus einem "undefined" in einem Array hier
			 * ein "null". Das muss entweder hier, oder ein paar Zeilen weiter oben nach
			 * _.isArray() beachtet werden.
			 */
			if (!_.isUndefined(new_model) && !_.isNull(new_model)) {
				let new_model_value = modelToLocal(new_model[prop]);
				/* der erste Fall ist: "wir haben lokal nichts geändert, und es kommt ein anderer Modellwert herein"
				 * der zweite Fall: "wir haben lokal editiert, und zufällig kommt unser Wert neu herein"
				 * Der zweite Fall passt meistens auf die Situation kurz nach Auslösen von "Übernehmen"
				 */
				if ((local[prop].in_sync && new_model_value !== local[prop].value) ||
					(!local[prop].in_sync && new_model_value === local[prop].value)) {
					if (!changes) {
						changes = {};
					}
					changes[prop] = makeLocal(new_model_value);
				}
			}
		}
	}
	return changes;
};

export const recheckState = (state, new_model, validate) => {
	let state_changes = null;
	if (state.model !== new_model) {
		state_changes = { model: new_model };
	}
	const local_changes = recheckLocal(state.local, new_model);
	if (local_changes) {
		state_changes = _.merge({}, state_changes, { local: local_changes });
	}
	if (!state_changes) {
		return null;
	}
	/* React.Component setState() arbeitet nicht rekursiv, deswegen den vollständigen
	 * neuen State zurück gegeben.
	 */
	let new_state = _.merge({}, state, state_changes);
	new_state.all_ok = validate(new_state.local);
	new_state.any_change = anyChange(new_state.local, new_model);
	return new_state;
};

/* Ordnet den Rückgabewert einer validateXYZ() Funktion ein,
 * ob der aktuelle lokale Wert in das Modell übernommen werden
 * kann.
 */
export const isOk = value => value !== "error";

export const validateHostname = (local, prop) => {
	if (!local[prop].value || !local[prop].value.length) {
		return "error";
	}
	return validateCheckOnlyModified(local, "hostname");
};

export const validateIp = (local, prop, is_required = true) => {
	const value = local[prop].value;
	if (!value) {
		return is_required ? "error"
			: validateCheckOnlyModified(local, prop);
	}
	const parts = value.split(".");
	if (parts.length !== 4) {
		return "error";
	}
	for (let i = 0; i < 4; ++i) {
		const n = parseInt(parts[i], 10);
		/* Der letzte Vergleich sorgt dafür, dass "1a" nicht aktzeptiert
		 * wird, weil parseInt nach "1" von sich aus schon aufhört.
		 */
		if (isNaN(n) || n < 0 || n > 255 || n.toString() !== parts[i]) {
			return "error";
		}
	}
	return validateCheckOnlyModified(local, prop);
};

export const validateDnsOrIp = (local, prop, is_required = true) => {
	let value = local[prop].value;
	if (!value) {
		return is_required ? "error"
			: validateCheckOnlyModified(local, prop);
	}
	/* Wegen der etwas unklaren Situation (Domain mit Umlauten, etc.)
	 * soll hier gar nicht so streng validiert werden. Leerzeichen
	 * sind hoffentlich noch lange Zeit verboten.
	 */
	if (value.indexOf(" ") !== -1) {
		return "error";
	}
	return validateCheckOnlyModified(local, prop);
};

export const validatePort = (local, prop) => {
	if (local[prop] === undefined) {
		return "error";
	}
	let value = local[prop].value;
	let n = parseInt(value, 10);
	if (isNaN(n) || n <= 0 || n > 65535 || n.toString() !== value) {
		return "error";
	}
	return validateCheckOnlyModified(local, prop);
};

export const validateEmail = (local, prop, is_required = true) => {
	let value = local[prop].value;
	if (!value) {
		return is_required ? "error"
			: validateCheckOnlyModified(local, prop);
	}
	let amp = value.indexOf("@");
	/* genau ein Klammeraffe */
	if (amp === -1 || value.indexOf("@", amp + 1) !== -1) {
		return "error";
	}
	/* User oder Domain zu kurz? */
	if (amp === 0 || amp === value.length - 1) {
		return "error";
	}
	let user = value.slice(0, amp);
	/* die "blacklisten" (beide) stammen aus dem Source-Code von Flex */
	const user_blacklist = "()<>,;:\\\"[] `~!#$%^&*={}|/?'\t\n\r";
	for (let u of user) {
		if (user_blacklist.indexOf(u) !== -1) {
			return "error";
		}
	}
	let domain = value.slice(amp + 1);
	const domain_blacklist = "()<>,;:\\\"[] `~!#$%^&*+={}|/?'\t\n\r";
	for (let d of domain) {
		if (domain_blacklist.indexOf(d) !== -1) {
			return "error";
		}
	}
	let domain_parts = domain.split(".");
	if (domain_parts.length < 2) {
		return "error";
	}
	for (let dp of domain_parts) {
		if (dp.length < 2) {
			return "error";
		}
	}
	/* weitere Tests spare ich mir heute */

	return validateCheckOnlyModified(local, prop);
};









export const modelValueToStateValue = (model, prop) => (
	!model ? undefined
		: _.isNumber(model[prop]) ? model[prop].toString() : model[prop]
);

export const modelToState = (props, model) => {
	let state = { model };
	for (let prop of props) {
		state[prop] = {
			/* Zahlen werden als Strings editiert */
			value: modelValueToStateValue(model, prop),
			/* TODO: "true" ist nicht immer richtig. Bei select Optionen
			* kann das Modell null oder undefined sein, wozu dann keine
			* der vorhandenen Optionen wirklich passt. Das select wird dann
			* unverändert angezeigt, wird seinen ersten Eintrag anzeigen
			* und bei der ersten Auswahl dann auf "modifiziert" gehen.
			* Gibt es eine einfache Lösung? ... select müsste "null" anzeigen
			* können, und als non-valid erkannt werden, oder? Dann würde das
			* "true" hier wieder stimmen.
			*/
			in_sync: true
		};
	}
	return state;
};

export const isInSync = (value, model, prop) => {
	let m = modelValueToStateValue(model, prop);
	return m === value;
};

export const changeValue = (event, state, validateAll, allProps) => {
	let prop = event.target.name;
	let value = event.target.value;
	let new_state = {
		[prop]: {
			value, in_sync: isInSync(value, state.model, prop)
		}
	};
	let next_state = _.assign({}, state, new_state);
	new_state.all_ok = validateAll(next_state);
	new_state.any_change = anyChange(allProps, next_state);
	return new_state;
};

export const validateCheckOnlyModified = (local, prop) => (
	local[prop].in_sync ? "success" : "warning"
);

export const validateNumber = (local, prop) => {
	if (local[prop] === undefined) {
		return "error";
	}
	let value = local[prop].value;
	let n = parseFloat(value);
	if (isNaN(n) || n < 0) {
		return "error";
	}
	return validateCheckOnlyModified(local, prop);
};
